<script setup lang="ts">
  import { vMaska } from 'maska/vue'

  type PhoneCodeNames = { es: string; en: string; fr: string; it: string; de: string }

  type PhoneCode = {
    code: string
    emoji: string
    dialCode: string
    mask: string
    names: PhoneCodeNames
  }

  type PhoneCodeItem = PhoneCode & { name: string; searchText: string }

  const props = withDefaults(
    defineProps<{
      fetchKey?: string
      codesUrl?: string
      defaultCountryCode?: string
      size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
      disabled?: boolean
      class?: string
    }>(),
    {
      fetchKey: 'phone-field-group-codes',
      codesUrl: '/api-web/phone-codes',
      defaultCountryCode: 'ES',
      size: 'lg',
      disabled: false,
      class: '',
    },
  )

  const { t, locale } = useI18n()

  const model = defineModel<string>({ default: '' })

  const { data: phoneCodes, status, execute } = await useLazyFetch<PhoneCode[]>(props.codesUrl, {
    key: props.fetchKey,
    immediate: false,
  })

  const countryCode = ref(props.defaultCountryCode)
  const phone = ref('')


  const phoneCodeItems = computed<PhoneCodeItem[]>(() => {
    const raw = phoneCodes.value
    if (!raw?.length) return []
    const k = locale.value
    return [...raw]
      .map((row) => {
        const n = row.names
        const display = n[k] ?? n.en ?? row.code
        const searchText = [row.code, row.dialCode, n.es, n.en, n.fr, n.it, n.de].filter(Boolean).join(' ').toLowerCase()
        return { ...row, name: display, searchText }
      })
      .sort((a, b) => a.name.localeCompare(b.name, locale.value, { sensitivity: 'base' }))
  })

  const country = computed(() => phoneCodeItems.value.find(c => c.code === countryCode.value))
  const dialCode = computed(() => country.value?.dialCode || '+34')
  const mask = computed(() => country.value?.mask || '### ### ###')

  /** Evita que `watch(countryCode)` borre la parte local al rellenar desde `v-model`. */
  let parsingModel = false

  function fullFromParts(): string {
    const local = phone.value.trim()
    return local ? `${dialCode.value} ${local}` : ''
  }

  function parseFromModel(full: string) {
    const codes = phoneCodes.value
    if (!codes?.length) return
    if (!full?.trim()) {
      phone.value = ''
      return
    }
    const trimmed = full.trim().replace(/\s+/g, ' ')
    const sorted = [...codes].sort((a, b) => b.dialCode.length - a.dialCode.length)
    const match = sorted.find(c => trimmed.startsWith(c.dialCode))
    parsingModel = true
    if (match) {
      countryCode.value = match.code
      phone.value = trimmed.slice(match.dialCode.length).trim()
    } else {
      phone.value = trimmed
    }
    nextTick(() => {
      parsingModel = false
    })
  }

  function onOpen(open: boolean) {
    if (open && !phoneCodes.value?.length) {
      void execute()
    }
  }

  watch(countryCode, () => {
    if (parsingModel) return
    phone.value = ''
  })

  watch([phone, dialCode], () => {
    if (!phoneCodes.value?.length) return
    const next = fullFromParts()
    if (model.value !== next) {
      model.value = next
    }
  })

  watch(model, (full) => {
    if (!phoneCodes.value?.length) return
    const current = fullFromParts().replace(/\s+/g, ' ').trim()
    const incoming = (full ?? '').replace(/\s+/g, ' ').trim()
    if (incoming === current) return
    parseFromModel(full ?? '')
  })

  watch(phoneCodes, codes => {
    if (codes?.length) {
      parseFromModel(model.value ?? '')
    }
  })

  onMounted(() => {
    void execute().then(() => parseFromModel(model.value ?? ''))
  })
</script>

<template>
  <UFieldGroup
    orientation="horizontal"
    :size="size"
    class="flex w-full items-stretch"
    :class="props.class"
  >
    <USelectMenu
      v-model="countryCode"
      class="flex-none shrink-0 rounded-l-lg"
      :items="phoneCodeItems"
      value-key="code"
      :size="size"
      :disabled="disabled"
      :search-input="{
        placeholder: t('buttons.buscar'),
        icon: 'i-lucide-search',
        loading: status === 'pending',
      }"
      :filter-fields="['name', 'code', 'dialCode', 'searchText']"
      :content="{ align: 'start' }"
      :ui="{
        base: 'max-w-fit pe-8',
        content: 'w-48',
        placeholder: 'hidden',
        trailingIcon: 'size-4',
      }"
      trailing-icon="i-lucide-chevrons-up-down"
      @update:open="onOpen"
    >
      <span class="flex size-5 items-center text-lg">
        {{ country?.emoji ?? '🇪🇸' }}
      </span>

      <template #item-leading="{ item }">
        <span class="flex size-5 items-center text-lg">
          {{ item.emoji }}
        </span>
      </template>

      <template #item-label="{ item }">
        {{ item.name }} ({{ item.dialCode }})
      </template>
    </USelectMenu>

    <UInput
      v-model="phone"
      v-maska="mask"
      :size="size"
      class="min-w-0 flex-1"
      :disabled="disabled"
      :placeholder="mask.replaceAll('#', '_')"
      :style="{ '--dial-code-length': `${dialCode.length + 1.5}ch` }"
      :ui="{
        base: 'ps-(--dial-code-length)',
        leading: 'pointer-events-none text-base md:text-sm text-muted',
      }"
    >
      <template #leading>
        {{ dialCode }}
      </template>
    </UInput>
  </UFieldGroup>
</template>
